import React, { createContext, useState, useContext, useEffect } from "react";
import { getProduct, getSettings } from "../services/api";

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("personalRegrets_cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [removedItems, setRemovedItems] = useState([]);

  useEffect(() => {
    localStorage.setItem("personalRegrets_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, size, quantity = 1) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (item) => item.product._id === product._id && item.size === size
      );

      if (existingItem) {
        return prevCart.map((item) =>
          item.product._id === product._id && item.size === size
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...prevCart, { product, size, quantity }];
    });
  };

  const removeFromCart = (productId, size) => {
    setCart((prevCart) =>
      prevCart.filter(
        (item) => !(item.product._id === productId && item.size === size)
      )
    );
  };

  const updateQuantity = (productId, size, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, size);
      return;
    }

    setCart((prevCart) =>
      prevCart.map((item) =>
        item.product._id === productId && item.size === size
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      return total + item.product.price * item.quantity;
    }, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const validateCart = async () => {
    if (cart.length === 0) return;
    try {
      const settingsRes = await getSettings();
      const { storeClosed } = settingsRes.data.settings;

      if (storeClosed) {
        // Tout le panier est bloqué, on ne retire rien mais on le signale
        return;
      }

      const removed = [];
      const updatedCart = [];

      for (const item of cart) {
        try {
          const productRes = await getProduct(item.product._id);
          const freshProduct = productRes.data;
          if (freshProduct.purchaseDisabled) {
            removed.push(item.product.name);
          } else {
            updatedCart.push(item);
          }
        } catch {
          // Produit introuvable → le retirer
          removed.push(item.product.name);
        }
      }

      if (removed.length > 0) {
        setCart(updatedCart);
        setRemovedItems(removed);
      }
    } catch {
      // Ignorer les erreurs réseau lors de la validation
    }
  };

  const clearRemovedItems = () => setRemovedItems([]);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        validateCart,
        removedItems,
        clearRemovedItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
